import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, FileText, Calendar, DollarSign, Grid3x3, List, Send, CheckCircle, Award, XCircle, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useIsCustomer } from '../hooks/usePortalMode';
import RFQCreateWizard from '../components/rfq/RFQCreateWizard';

interface RFQ {
  id: number;
  rfq_number: string;
  title: string;
  description: string;
  customer: number;
  customer_name: string;
  supplier: number | null;
  supplier_name: string | null;
  status: string;
  priority: string;
  category: string;
  currency: string;
  total_estimated_value: string;
  response_deadline: string;
  created_at: string;
}

type StatusTab = 'all' | 'draft' | 'sent' | 'submitted' | 'awarded' | 'cancelled';

export default function RFQ() {
  const { formatCurrency: formatCurrencyFromHook, formatDate } = useFormatters()
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const [rfqs, setRfqs] = useState<RFQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [stats, setStats] = useState({ total: 0, draft: 0, sent: 0, submitted: 0, awarded: 0, cancelled: 0 });
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  useEffect(() => {
    fetchRFQs();
    fetchStats();
  }, [statusTab]);

  const fetchRFQs = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusTab !== 'all') {
        params['status'] = statusTab;
      }
      const response = await api.get('/rfq/rfqs/', { params });
      setRfqs(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching RFQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/rfq/rfqs/dashboard_stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreate = () => {
    setShowCreateModal(false);
    fetchRFQs();
    fetchStats();
  };

  const formatCurrency = (amount: string, currency: string) => {
    return formatCurrencyFromHook(parseFloat(amount), currency);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      submitted: 'bg-yellow-100 text-yellow-800',
      awarded: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-300 text-gray-700',
    };
    return colors[status] || 'bg-gray-100';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <FileText className="h-3 w-3" />;
      case 'sent': return <Send className="h-3 w-3" />;
      case 'submitted': return <CheckCircle className="h-3 w-3" />;
      case 'awarded': return <Award className="h-3 w-3" />;
      case 'cancelled': return <XCircle className="h-3 w-3" />;
      default: return null;
    }
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      urgent: 'bg-red-100 text-red-600',
    };
    return colors[priority] || 'bg-gray-100';
  };

  const filteredRFQs = rfqs.filter(rfq =>
    rfq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rfq.rfq_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show wizard as full page content when creating
  if (showCreateModal) {
    return (
      <RFQCreateWizard
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">RFQ Management</h1>
          <p className="text-muted-foreground">Manage Requests for Quotation and track supplier responses</p>
        </div>
        {isCustomer && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create RFQ
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className={statusTab === 'all' ? 'border-primary' : ''}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setStatusTab('all')}>
            <CardDescription>Total</CardDescription>
            <CardTitle className="text-2xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card className={statusTab === 'draft' ? 'border-primary' : ''}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setStatusTab('draft')}>
            <CardDescription className="flex items-center gap-1">
              <FileText className="h-3 w-3" /> Draft
            </CardDescription>
            <CardTitle className="text-2xl text-gray-600">{stats.draft}</CardTitle>
          </CardHeader>
        </Card>
        <Card className={statusTab === 'sent' ? 'border-primary' : ''}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setStatusTab('sent')}>
            <CardDescription className="flex items-center gap-1">
              <Send className="h-3 w-3" /> Sent
            </CardDescription>
            <CardTitle className="text-2xl text-blue-600">{stats.sent}</CardTitle>
          </CardHeader>
        </Card>
        <Card className={statusTab === 'submitted' ? 'border-primary' : ''}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setStatusTab('submitted')}>
            <CardDescription className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3" /> Submitted
            </CardDescription>
            <CardTitle className="text-2xl text-yellow-600">{stats.submitted || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className={statusTab === 'awarded' ? 'border-primary' : ''}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setStatusTab('awarded')}>
            <CardDescription className="flex items-center gap-1">
              <Award className="h-3 w-3" /> Awarded
            </CardDescription>
            <CardTitle className="text-2xl text-green-600">{stats.awarded || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className={statusTab === 'cancelled' ? 'border-primary' : ''}>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setStatusTab('cancelled')}>
            <CardDescription className="flex items-center gap-1">
              <XCircle className="h-3 w-3" /> Cancelled
            </CardDescription>
            <CardTitle className="text-2xl text-gray-400">{stats.cancelled || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Status Tabs */}
      <Tabs value={statusTab} onValueChange={(value) => setStatusTab(value as StatusTab)}>
        <TabsList>
          <TabsTrigger value="all">All RFQs</TabsTrigger>
          <TabsTrigger value="draft">Draft</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="submitted">Submitted</TabsTrigger>
          <TabsTrigger value="awarded">Awarded</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search & View Toggle */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by RFQ number or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="flex gap-1">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <Grid3x3 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* RFQ List/Grid */}
      <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-4`}>
        {loading ? (
          <div className="col-span-full text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">Loading RFQs...</p>
          </div>
        ) : filteredRFQs.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground">No RFQs found. Create your first RFQ to get started.</p>
            </CardContent>
          </Card>
        ) : (
          filteredRFQs.map((rfq) => (
            <Card
              key={rfq.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/rfq/${rfq.id}`)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{rfq.rfq_number}</CardTitle>
                      <Badge className={getStatusColor(rfq.status)}>
                        {getStatusIcon(rfq.status)}
                        <span className="ml-1">{rfq.status.toUpperCase()}</span>
                      </Badge>
                      <Badge className={getPriorityColor(rfq.priority)}>{rfq.priority.toUpperCase()}</Badge>
                    </div>
                    <CardDescription className="font-medium text-base text-foreground">{rfq.title}</CardDescription>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{rfq.description}</p>
                  </div>
                  {viewMode === 'list' && (
                    <div className="text-right space-y-1 ml-4">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Due: {formatDate(rfq.response_deadline)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm font-semibold">
                        <DollarSign className="h-4 w-4" />
                        <span>{formatCurrency(rfq.total_estimated_value, rfq.currency)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 text-sm text-muted-foreground flex-wrap">
                  <span>Category: <span className="font-medium text-foreground capitalize">{rfq.category}</span></span>
                  {viewMode === 'grid' && (
                    <span>Value: <span className="font-medium text-foreground">{formatCurrency(rfq.total_estimated_value, rfq.currency)}</span></span>
                  )}
                  <span className="ml-auto">Created: {formatDate(rfq.created_at)}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
